import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Lock, MapPin, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ButtonLabel } from "../../components/ButtonLabel";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest, describeApiError } from "../../lib/api";
import { currentPosition } from "../../lib/geo";
import { isValidCurp, normalizeCurp } from "../../lib/loans";
import { useKyc, useKycCatalogue } from "../../lib/queries";
import type { ApiEnvelope, KycProfile, PostalCodeLookup } from "../../types";

/**
 * "Tus datos para Kuspit": what Kuspit Casa de Bolsa opens the customer's contract with — names as
 * they appear on the CURP, mobile, birth date and place, and the address picked from Kuspit's own
 * catalogue (postal code → municipality → colonia). Kuspit checks the CURP against the birth date
 * and initials, so the same checks show here before anything is sent. Once Kuspit has opened the
 * contract the data is locked (support corrects it).
 */
export function KycProfilePage() {
  usePageHeader("Tus datos", "Kuspit Casa de Bolsa abre tu cuenta de inversión con esta información");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("siguiente");
  const kyc = useKyc();
  const countries = useKycCatalogue("countries");

  const [form, setForm] = useState({
    firstName: "", paternalSurname: "", maternalSurname: "", mobilePhone: "", birthDate: "", sex: "" as "" | "H" | "M",
    curp: "", rfc: "", birthCountryId: "187", birthStateId: "", postalCode: "", municipalityId: "", colonyId: "",
    street: "", exteriorNumber: "", interiorNumber: ""
  });
  const [loaded, setLoaded] = useState(false);
  const [address, setAddress] = useState<PostalCodeLookup | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  // Every country in Kuspit's catalogue lists its own states (one, the UK, lists none).
  const birthStates = useKycCatalogue("states", { idPais: form.birthCountryId }, Boolean(form.birthCountryId));
  const needsBirthState = (birthStates.data?.length ?? 0) > 0;

  const profile = kyc.data?.profile ?? null;
  useEffect(() => {
    if (loaded || !kyc.data) return;
    setLoaded(true);
    if (!profile) return;
    setForm({
      firstName: profile.firstName, paternalSurname: profile.paternalSurname, maternalSurname: profile.maternalSurname ?? "",
      mobilePhone: profile.mobilePhone, birthDate: profile.birthDate, sex: profile.sex, curp: profile.curp, rfc: profile.rfc ?? "",
      birthCountryId: profile.birthCountryId, birthStateId: profile.birthStateId ?? "", postalCode: profile.postalCode,
      municipalityId: profile.municipalityId, colonyId: profile.colonyId, street: profile.street, exteriorNumber: profile.exteriorNumber,
      interiorNumber: profile.interiorNumber ?? ""
    });
  }, [kyc.data, loaded, profile]);

  // Postal code → Kuspit's address catalogue, as soon as five digits are typed.
  useEffect(() => {
    if (!/^\d{5}$/.test(form.postalCode)) { setAddress(null); return; }
    let cancelled = false;
    setLookingUp(true); setAddressError(null);
    apiRequest<ApiEnvelope<PostalCodeLookup>>(`/api/v1/kyc/postal-codes/${form.postalCode}`)
      .then((payload) => {
        if (cancelled) return;
        setAddress(payload.data);
        const municipalities = payload.data.municipalities;
        setForm((current) => {
          const municipality = municipalities.find((entry) => entry.id === current.municipalityId) ?? (municipalities.length === 1 ? municipalities[0] : undefined);
          const colony = municipality?.colonies.find((entry) => entry.id === current.colonyId) ?? (municipality?.colonies.length === 1 ? municipality.colonies[0] : undefined);
          return { ...current, municipalityId: municipality?.id ?? "", colonyId: colony?.id ?? "" };
        });
      })
      .catch((cause) => { if (!cancelled) { setAddress(null); setAddressError(describeApiError(cause, "No encontramos ese código postal.")[0] ?? null); } })
      .finally(() => { if (!cancelled) setLookingUp(false); });
    return () => { cancelled = true; };
  }, [form.postalCode]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));
  const municipality = address?.municipalities.find((entry) => entry.id === form.municipalityId) ?? null;
  const curpOk = isValidCurp(form.curp);
  // The CURP carries the birth date and sex: shown as soon as both are typed.
  const curpMismatch = useMemo(() => {
    if (!curpOk || !form.birthDate) return null;
    const [year, month, day] = form.birthDate.split("-");
    if (form.curp.slice(4, 10) !== `${year!.slice(2)}${month}${day}`) return "La fecha de nacimiento no coincide con tu CURP.";
    if (form.sex && form.curp[10] !== form.sex) return "El sexo no coincide con tu CURP.";
    return null;
  }, [curpOk, form.curp, form.birthDate, form.sex]);

  if (kyc.isLoading) return <div className="page-state"><Spinner label="Cargando tus datos" /></div>;
  if (kyc.isError) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No pudimos cargar tus datos</strong></div>;
  const locked = kyc.data?.profileLocked ?? false;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null); setWarnings([]);
    if (!form.sex) { setError(["Selecciona tu sexo como aparece en tu CURP."]); return; }
    if (curpMismatch) { setError([curpMismatch]); return; }
    setSubmitting(true);
    try {
      const location = await currentPosition();
      const payload = await apiRequest<ApiEnvelope<{ profile: KycProfile; warnings: string[] }>>("/api/v1/kyc/profile", {
        method: "PUT",
        body: JSON.stringify({ ...form, ...location, birthStateId: needsBirthState ? form.birthStateId : undefined })
      });
      await queryClient.invalidateQueries({ queryKey: ["kyc"] });
      if (payload.data.warnings.length) { setWarnings(payload.data.warnings); return; }
      navigate(next && next.startsWith("/") ? next : "/wallet");
    } catch (cause) {
      setError(cause instanceof Error && !("code" in cause) ? [cause.message] : describeApiError(cause, "No pudimos guardar tus datos."));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="page-container">
      <form className="card loan-form" onSubmit={submit} noValidate style={{ maxWidth: 760, margin: "0 auto", display: "grid", gap: 22 }}>
        <div>
          <span className="loan-kicker"><ShieldCheck size={14} aria-hidden="true" /> Verificación con Kuspit</span>
          <h2 style={{ margin: "6px 0 4px" }}>Tus datos personales</h2>
          <p className="field-hint" style={{ fontSize: 13 }}>
            Escríbelos exactamente como aparecen en tu CURP e identificación oficial. Kuspit los valida contra RENAPO antes de abrir tu cuenta.
          </p>
        </div>
        {locked && (
          <div className="form-message" role="status"><Lock size={14} aria-hidden="true" /> Kuspit ya abrió tu cuenta con estos datos. Si algo está mal, escríbenos a soporte para corregirlo.</div>
        )}

        <fieldset disabled={locked || submitting} style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 18 }}>
          <div className="field-grid three">
            <Field id="k-first" label="Nombre(s)"><input id="k-first" className="standalone-input" value={form.firstName} onChange={(event) => set("firstName", event.target.value)} autoComplete="given-name" required /></Field>
            <Field id="k-pat" label="Apellido paterno"><input id="k-pat" className="standalone-input" value={form.paternalSurname} onChange={(event) => set("paternalSurname", event.target.value)} autoComplete="family-name" required /></Field>
            <Field id="k-mat" label="Apellido materno" hint="Si no tienes, déjalo vacío."><input id="k-mat" className="standalone-input" value={form.maternalSurname} onChange={(event) => set("maternalSurname", event.target.value)} /></Field>
          </div>
          <div className="field-grid three">
            <Field id="k-birth" label="Fecha de nacimiento"><input id="k-birth" type="date" className="standalone-input" value={form.birthDate} onChange={(event) => set("birthDate", event.target.value)} max={new Date().toISOString().slice(0, 10)} required /></Field>
            <Field id="k-sex" label="Sexo (como en tu CURP)">
              <select id="k-sex" className="standalone-input" value={form.sex} onChange={(event) => set("sex", event.target.value as "H" | "M")} required>
                <option value="">Selecciona</option><option value="M">Mujer</option><option value="H">Hombre</option>
              </select>
            </Field>
            <Field id="k-phone" label="Celular" hint="10 dígitos.">
              <input id="k-phone" className="standalone-input" inputMode="numeric" value={form.mobilePhone} maxLength={10} onChange={(event) => set("mobilePhone", event.target.value.replace(/\D/g, "").slice(0, 10))} autoComplete="tel-national" required />
            </Field>
          </div>
          <div className="field-grid">
            <Field id="k-curp" label="CURP" hint={curpMismatch ?? (form.curp && !curpOk ? "Revisa tu CURP: 18 caracteres con formato válido." : "La encuentras en tu INE o en gob.mx/curp.")} error={Boolean(curpMismatch) || (form.curp.length === 18 && !curpOk)}>
              <input id="k-curp" className="standalone-input" value={form.curp} maxLength={18} autoCapitalize="characters" spellCheck={false} translate="no" onChange={(event) => set("curp", normalizeCurp(event.target.value))} required />
            </Field>
            <Field id="k-rfc" label="RFC con homoclave (opcional)" hint="13 caracteres. Los primeros 10 coinciden con tu CURP.">
              <input id="k-rfc" className="standalone-input" value={form.rfc} maxLength={13} autoCapitalize="characters" spellCheck={false} translate="no" onChange={(event) => set("rfc", event.target.value.toUpperCase().replace(/[^A-ZÑ&0-9]/g, "").slice(0, 13))} />
            </Field>
          </div>
          <div className="field-grid">
            <Field id="k-bcountry" label="País de nacimiento">
              <select id="k-bcountry" className="standalone-input" value={form.birthCountryId} onChange={(event) => { const id = event.target.value; setForm((current) => ({ ...current, birthCountryId: id, birthStateId: "" })); }}>
                {(countries.data ?? [{ id: "187", label: "MÉXICO" }]).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
            </Field>
            {needsBirthState && (
              <Field id="k-bstate" label="Estado de nacimiento">
                <select id="k-bstate" className="standalone-input" value={form.birthStateId} onChange={(event) => set("birthStateId", event.target.value)} required>
                  <option value="">Selecciona</option>
                  {(birthStates.data ?? []).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </Field>
            )}
          </div>

          <h3 style={{ margin: "6px 0 0" }}>Domicilio</h3>
          <div className="field-grid three">
            <Field id="k-cp" label="Código postal" hint={lookingUp ? "Buscando…" : addressError ?? (address?.state ? `${address.state.label}` : "5 dígitos.")} error={Boolean(addressError)}>
              <input id="k-cp" className="standalone-input" inputMode="numeric" value={form.postalCode} maxLength={5} translate="no" onChange={(event) => set("postalCode", event.target.value.replace(/\D/g, "").slice(0, 5))} autoComplete="postal-code" required />
            </Field>
            <Field id="k-mun" label="Municipio o alcaldía">
              <select id="k-mun" className="standalone-input" value={form.municipalityId} onChange={(event) => { set("municipalityId", event.target.value); set("colonyId", ""); }} disabled={!address} required>
                <option value="">{address ? "Selecciona" : "Escribe tu código postal"}</option>
                {(address?.municipalities ?? []).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
            </Field>
            <Field id="k-col" label="Colonia">
              <select id="k-col" className="standalone-input" value={form.colonyId} onChange={(event) => set("colonyId", event.target.value)} disabled={!municipality} required>
                <option value="">{municipality ? "Selecciona" : "Primero el municipio"}</option>
                {(municipality?.colonies ?? []).map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="field-grid three">
            <Field id="k-street" label="Calle"><input id="k-street" className="standalone-input" value={form.street} onChange={(event) => set("street", event.target.value)} autoComplete="address-line1" required /></Field>
            <Field id="k-ext" label="Número exterior"><input id="k-ext" className="standalone-input" value={form.exteriorNumber} onChange={(event) => set("exteriorNumber", event.target.value)} required /></Field>
            <Field id="k-int" label="Número interior (opcional)"><input id="k-int" className="standalone-input" value={form.interiorNumber} onChange={(event) => set("interiorNumber", event.target.value)} /></Field>
          </div>
        </fieldset>

        {warnings.length > 0 && (
          <div className="form-message" role="status">
            {warnings.join(" ")} Guardamos tus datos; si están bien escritos, continúa.
            <div style={{ marginTop: 8 }}><button type="button" className="secondary-button small" onClick={() => navigate(next && next.startsWith("/") ? next : "/wallet")}>Continuar</button></div>
          </div>
        )}
        {error && <div className="form-error" role="alert">{error.length === 1 ? error[0] : <ul className="form-error-list">{error.map((message) => <li key={message}>{message}</li>)}</ul>}</div>}

        {!locked && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span className="field-hint"><MapPin size={12} aria-hidden="true" /> Al guardar te pediremos tu ubicación: Kuspit la requiere para abrir tu cuenta.</span>
            <button type="submit" className="primary-button" disabled={submitting}><ButtonLabel busy={submitting} busyLabel="Guardando" label="Guardar mis datos" /></button>
          </div>
        )}
        {locked && <Link className="secondary-button" to="/verificacion/expediente">Ir a mi expediente</Link>}
      </form>
    </div>
  );
}

function Field({ id, label, hint, error = false, children }: { id: string; label: string; hint?: string | null; error?: boolean; children: React.ReactNode }) {
  return (
    <div className="field-block">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className={`field-hint${error ? " error" : ""}`} role={error ? "alert" : undefined}>{hint}</span>}
    </div>
  );
}
